import api from '@/lib/api';
import type { FieldsToType } from '@/lib/type-utils';
import { useAuthStore } from '@/store/authStore';
import type { FieldDefinition } from '@/types/fields-api.model';
import type { User, UserSubscription } from '@/types/user.model';
import { generateFields } from '../generators-api';
import { USER_FIELDS } from './user-fields.config';
import { createSingleApiWithFields, HttpMethod } from '@/lib/api-base';

// Variable to store the promise of the ongoing user request
let userFetchPromise: Promise<User> | null = null;

// Bumped on every identity change (see `session-caches.ts`). A fetch that was
// already in flight when the session switched must not write its profile into
// the new one: it describes the account that is no longer signed in. Dropping
// the promise is not enough on its own — the request keeps running and its
// `.then` would still reach `setUser`.
let userFetchGeneration = 0;

/**
 * Invalidates the single-flight above: the next caller starts a fresh request,
 * and whatever is still in flight is barred from writing to the store.
 *
 * Called on every transition into or out of a support session, where the
 * profile that comes back belongs to a different account than the one that
 * asked for it.
 */
export const resetUserFetch = () => {
  userFetchGeneration += 1;
  userFetchPromise = null;
};

const updateUserWithFields = createSingleApiWithFields<
  User,
  typeof USER_FIELDS
>('/users/edit', USER_FIELDS, HttpMethod.PATCH);

/**
 * Gets user data with dynamic typing based on field selectors.
 * This is the base method that makes the actual API call.
 *
 * @param fields A field selector object from USER_FIELDS.
 * @returns A promise that resolves to user data, typed according to the fields selector.
 */
export const getUserData = async <T extends object = typeof USER_FIELDS.FULL>(
  fields: T = USER_FIELDS.FULL as unknown as T,
): Promise<FieldsToType<T, User>> => {
  const response = await api.get('/users', {
    params: {
      fields: generateFields(fields as FieldDefinition),
    },
  });

  return response.data;
};

export const userService = {
  /**
   * Gets the complete information of the authenticated user
   * Implements a "single flight" pattern to prevent multiple concurrent API calls.
   * Always returns the full User object and uses cache.
   */
  getCurrentUser: async (): Promise<User> => {
    const { user, setUser, setIsLoadingUser } = useAuthStore.getState();

    // 1. If the user is already in the store, return it immediately.
    if (user) {
      return user;
    }

    // 2. If there's already a request in progress, return that same promise.
    if (userFetchPromise) {
      return userFetchPromise;
    }

    // 3. No user in store and no request in progress, initiate a new one.
    setIsLoadingUser(true); // Mark that the request is in progress

    const generation = userFetchGeneration;
    const isCurrentSession = () => generation === userFetchGeneration;

    const request: Promise<User> = getUserData(USER_FIELDS.FULL)
      .then((response) => {
        // Never write a profile fetched under a session that has since been
        // replaced: on the way out of a support session that would install the
        // impersonated user as the admin, dropping the ADMIN role and with it
        // the backoffice, the banner and the way back.
        if (isCurrentSession()) {
          setUser(response); // Store the user in the store
        }
        return response;
      })
      .catch((error) => {
        console.error('Error fetching user information:', error);
        // If the request fails, clear the user and throw the error
        if (isCurrentSession()) {
          setUser(null);
        }
        throw error;
      })
      .finally(() => {
        // Only whoever still holds the single-flight slot may lower the flag —
        // a stale request settling after a newer one started would announce
        // "done" for a fetch that is still open. When the slot was dropped by a
        // session switch and nothing has claimed it yet, the flag still has to
        // come down: `useAuth` only fetches the new session's profile while
        // `isLoadingUser` is false.
        if (userFetchPromise === request) {
          setIsLoadingUser(false); // Mark that the request has finished
          userFetchPromise = null; // Clear the promise for future requests
        } else if (!userFetchPromise) {
          setIsLoadingUser(false);
        }
      });

    userFetchPromise = request;
    return request;
  },

  /**
   * Gets the user roles
   */
  getUserRoles: async (): Promise<string[]> => {
    const { user } = useAuthStore.getState();
    if (user) {
      return user.roles; // Get roles from stored user
    }
    // If the user is not in the store, get it (this will use the single flight pattern)
    try {
      const currentUser = await userService.getCurrentUser();
      return currentUser.roles;
    } catch (error) {
      console.error('Error fetching user roles:', error);
      return [];
    }
  },

  /**
   * Checks if the user has a specific role
   */
  hasRole: async (role: string): Promise<boolean> => {
    const { user } = useAuthStore.getState();
    if (user) {
      return user.roles.includes(role); // Check role from stored user
    }
    // If the user is not in the store, get it (this will use the single flight pattern)
    try {
      const roles = await userService.getUserRoles();
      return roles.includes(role);
    } catch (error) {
      console.error('Error checking user role:', error);
      return false;
    }
  },

  /**
   * Gets the user subscriptions
   */
  getUserSubscriptions: async (): Promise<{
    player: UserSubscription;
    manager: UserSubscription;
  }> => {
    const { user } = useAuthStore.getState();
    if (user) {
      return {
        player: user.playerSubscription,
        manager: user.managerSubscription,
      }; // Get subscriptions from stored user
    }
    // If the user is not in the store, get it (this will use the single flight pattern)
    try {
      const currentUser = await userService.getCurrentUser();
      return {
        player: currentUser.playerSubscription,
        manager: currentUser.managerSubscription,
      };
    } catch (error) {
      console.error('Error fetching user subscriptions:', error);
      throw error;
    }
  },

  /**
   * Checks if the user has Mercado Pago credentials
   */
  hasMercadoPagoCredentials: async (): Promise<boolean> => {
    const { user } = useAuthStore.getState();
    if (user) {
      return user.hasMercadoPagoCredentials; // Get from stored user
    }
    // If the user is not in the store, get it (this will use the single flight pattern)
    try {
      const currentUser = await userService.getCurrentUser();
      return currentUser.hasMercadoPagoCredentials;
    } catch (error) {
      console.error('Error checking Mercado Pago credentials:', error);
      return false;
    }
  },

  /**
   * Re-reads the profile of the session already installed while the cached one
   * stays on screen. Unlike `refreshCurrentUser` it never clears the user nor
   * raises `isLoadingUser`, so the guards do not swap the whole layout for a
   * LoadingScreen — for a token swap on the same account, where the roles can
   * change but the page the admin is on should not reset.
   */
  reloadCurrentUserInPlace: async (): Promise<User> => {
    const generation = userFetchGeneration;
    const response = await getUserData(USER_FIELDS.FULL);
    if (generation === userFetchGeneration) {
      useAuthStore.getState().setUser(response);
    }
    return response;
  },

  /**
   * Forces a fresh fetch of user data, clearing any cached data
   * Useful after operations that modify user state (like MercadoPago authorization)
   */
  refreshCurrentUser: async (): Promise<User> => {
    const { setUser } = useAuthStore.getState();

    // Clear the current user and any ongoing request to force a fresh fetch
    setUser(null);
    resetUserFetch();

    // Now fetch fresh user data
    return userService.getCurrentUser();
  },

  /**
   * Requests a password reset for the given email
   */
  requestPasswordReset: async (email: string): Promise<void> => {
    await api.post('/users/password/reset/request', { email });
  },

  searchPlayers: async (query: string): Promise<User[]> => {
    const response = await api.get('/users/find-users', {
      params: {
        query,
        fields: generateFields(USER_FIELDS.FIND as FieldDefinition),
      },
    });
    return response.data;
  },

  /**
   * Confirms the password reset with the token and new password
   */
  confirmPasswordReset: async (
    token: string,
    newPassword: string,
  ): Promise<void> => {
    await api.post('/users/password/reset/confirm', {
      token,
      newPassword,
    });
  },

  /**
   * Changes the signed-in user's password. The backend answers 400 when the
   * current password does not match.
   */
  changePassword: async (
    currentPassword: string,
    newPassword: string,
  ): Promise<void> => {
    await api.patch('/users/change-password', {
      currentPassword,
      newPassword,
    });
  },

  /**
   * Updates user profile information using the standardized field-based API pattern
   * @param userData - User data fields to update
   * @returns Promise that resolves to the updated user with complete information
   */
  updateUser: async (
    userData: Pick<
      User,
      | 'email'
      | 'name'
      | 'gender'
      | 'birthdate'
      | 'cellphone'
      | 'id'
      | 'username'
    >,
  ): Promise<User> => {
    const { setUser, setIsLoadingUser } = useAuthStore.getState();

    setIsLoadingUser(true);

    try {
      const response = await updateUserWithFields(USER_FIELDS.FULL, userData);

      setUser(response as User);

      return response as User;
    } catch (error) {
      console.error('Error updating user information:', error);
      throw error;
    } finally {
      setIsLoadingUser(false);
    }
  },

  /**
   * Accepts the terms and conditions for the current user
   * Updates the termsAccepted field to true
   * @returns Promise that resolves to the updated user
   */
  acceptTerms: async (): Promise<User> => {
    try {
      await updateUserWithFields(USER_FIELDS.FULL, { termsAccepted: true });
      return await userService.refreshCurrentUser();
    } catch (error) {
      console.error('Error accepting terms:', error);
      throw error;
    }
  },
};
